import { useState, useEffect } from 'react';
import jm from 'jalali-moment';
import { bookServiceApi } from '@/services/api';
import { getValidAccessToken } from '@/services/authService';
import type { WeekDay } from '../types/streak';

interface StreakApiResponse {
  dates: string[];
  freeze_dates: string[];
  max_streak: number;
  current_streak: number;
  distance_to_record: number;
  today_minutes?: number;
}

interface UseStreakDataReturn {
  currentStreak: number;
  maxStreak: number;
  todayMinutes: number;
  totalDays: number;
  weeklyProgress: WeekDay[];
  isLoading: boolean;
  error: string | null;
}

interface UseStreakDataOptions {
  fallbackCurrentStreak?: number;
}

const toPersianDigits = (value: number | string): string =>
  String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

export const useStreakData = ({
  fallbackCurrentStreak = 0,
}: UseStreakDataOptions = {}): UseStreakDataReturn => {
  const [currentStreak, setCurrentStreak] = useState(fallbackCurrentStreak);
  const [maxStreak, setMaxStreak] = useState(0);
  const [todayMinutes, setTodayMinutes] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [weeklyProgress, setWeeklyProgress] = useState<WeekDay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateWeeklyProgress = (completedDates: string[], freezeDates: string[]): WeekDay[] => {
    const today = jm().locale('fa');
    const todayKey = `${today.jYear()}-${String(today.jMonth() + 1).padStart(2, '0')}-${String(
      today.jDate()
    ).padStart(2, '0')}`;
    const startOfWeek = today.clone().startOf('week');
    const dayNames = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

    return Array.from({ length: 7 }, (_, index) => {
      const currentDay = startOfWeek.clone().add(index, 'day');
      const dayKey = `${currentDay.jYear()}-${String(currentDay.jMonth() + 1).padStart(2, '0')}-${String(
        currentDay.jDate()
      ).padStart(2, '0')}`;
      const isToday = dayKey === todayKey;
      const isCompleted = completedDates.includes(dayKey);
      const isFreeze = freezeDates.includes(dayKey);
      const isFuture = currentDay.isAfter(today, 'day');

      return {
        day: dayNames[index],
        date: toPersianDigits(currentDay.jDate()),
        isCompleted,
        isFreeze,
        isToday,
        hasActivity: !isFuture,
      };
    });
  };

  useEffect(() => {
    const fetchStreakData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = await getValidAccessToken();
        if (!token) {
          setCurrentStreak(fallbackCurrentStreak);
          setWeeklyProgress(generateWeeklyProgress([], []));
          setError('برای مشاهده اطلاعات، لطفاً وارد شوید.');
          setIsLoading(false);
          return;
        }

        const response = await bookServiceApi.get<StreakApiResponse>('/completed-sections/dates');

        if (response.success && response.data) {
          const { current_streak, max_streak, dates, freeze_dates, today_minutes } = response.data;

          setCurrentStreak(current_streak ?? fallbackCurrentStreak);
          setMaxStreak(max_streak || 0);
          setTodayMinutes(today_minutes || 0);
          setTotalDays(dates?.length || 0);
          setWeeklyProgress(generateWeeklyProgress(dates || [], freeze_dates || []));
          return;
        }

        setCurrentStreak(fallbackCurrentStreak);
        setWeeklyProgress(generateWeeklyProgress([], []));
        setError(response.error || 'خطا در دریافت اطلاعات پیوستگی');
      } catch (err: any) {
        setCurrentStreak(fallbackCurrentStreak);
        setWeeklyProgress(generateWeeklyProgress([], []));
        setError(err?.message || 'خطای ناشناخته در دریافت اطلاعات');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreakData();
  }, [fallbackCurrentStreak]);

  return {
    currentStreak,
    maxStreak,
    todayMinutes,
    totalDays,
    weeklyProgress,
    isLoading,
    error,
  };
};
